// src/components/admin/UserManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, updateDoc, deleteDoc,
  query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Users, Shield, Mail, Calendar, Search, Filter, AlertTriangle } from 'lucide-react';

const PURGE_COLLECTIONS = ['subscriptions', 'creator_verifications', 'podcast_verifications'];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [purgeInput, setPurgeInput] = useState('');
  const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userData = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(userData.sort((a, b) => b.dateJoined?.seconds - a.dateJoined?.seconds));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, updates) => {
    try {
      await updateDoc(doc(db, 'users', userId), { ...updates, lastUpdated: new Date() });
      setUsers(users.map(u => u.id === userId ? { ...u, ...updates } : u));
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Fejl ved opdatering af bruger');
    }
  };

  const suspendUser = async (userId) => {
    setActionLoading(true);
    try {
      const updates = { accountStatus: 'suspended', isSuspended: true, lastUpdated: serverTimestamp() };
      await updateDoc(doc(db, 'users', userId), updates);
      setUsers(users.map(u => u.id === userId ? { ...u, ...updates } : u));
      setSelectedUser(null);
    } catch (error) {
      console.error('Error suspending user:', error);
      alert('Fejl ved suspendering af bruger');
    } finally {
      setActionLoading(false);
    }
  };

  const unsuspendUser = async (userId) => {
    setActionLoading(true);
    try {
      const updates = { accountStatus: 'active', isSuspended: false, lastUpdated: serverTimestamp() };
      await updateDoc(doc(db, 'users', userId), updates);
      setUsers(users.map(u => u.id === userId ? { ...u, ...updates } : u));
      setSelectedUser(null);
    } catch (error) {
      console.error('Error unsuspending user:', error);
      alert('Fejl ved ophævelse af suspendering');
    } finally {
      setActionLoading(false);
    }
  };

  const softDeleteUser = async (userId) => {
    if (!window.confirm('Fjern denne brugers profil? Kontoen bevares i Firebase Auth men profilen markeres som slettet.')) return;
    setActionLoading(true);
    try {
      const updates = { accountStatus: 'removed', isDeleted: true, deletedAt: serverTimestamp(), lastUpdated: serverTimestamp() };
      await updateDoc(doc(db, 'users', userId), updates);
      setUsers(users.map(u => u.id === userId ? { ...u, ...updates } : u));
      setSelectedUser(null);
    } catch (error) {
      console.error('Error removing user:', error);
      alert('Fejl ved fjernelse af bruger');
    } finally {
      setActionLoading(false);
    }
  };

  const purgeUser = async (userId, username) => {
    if (purgeInput !== username) return;
    setActionLoading(true);
    try {
      // Delete the main user profile
      await deleteDoc(doc(db, 'users', userId));

      // Delete docs in simple collections keyed by uid
      for (const col of PURGE_COLLECTIONS) {
        try { await deleteDoc(doc(db, col, userId)); } catch (_) { /* doc may not exist */ }
      }

      // Delete user_podcasts where userId matches
      const podcastsSnap = await getDocs(query(collection(db, 'user_podcasts'), where('userId', '==', userId)));
      await Promise.all(podcastsSnap.docs.map(d => deleteDoc(d.ref)));

      setUsers(users.filter(u => u.id !== userId));
      setSelectedUser(null);
      setPurgeConfirmOpen(false);
      setPurgeInput('');
      alert(`Brugerdata for "${username}" er slettet fra Firestore.\n\nNB: Firebase Auth-kontoen skal slettes manuelt i Firebase Console eller via Admin SDK.`);
    } catch (error) {
      console.error('Error purging user:', error);
      alert('Fejl ved sletning af brugerdata: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filter === 'all' ||
      (filter === 'creators' && user.isCreator) ||
      (filter === 'listeners' && !user.isCreator && !user.isAdmin) ||
      (filter === 'admins' && user.isAdmin) ||
      (filter === 'suspended' && user.isSuspended) ||
      (filter === 'removed' && user.isDeleted);

    return matchesSearch && matchesFilter;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Ukendt';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getUserRole = (user) => {
    if (user.isAdmin) return { text: 'Administrator', color: 'text-red-400 bg-red-600/20' };
    if (user.isCreator) return { text: 'Skaber', color: 'text-blue-400 bg-blue-600/20' };
    return { text: 'Lytter', color: 'text-green-400 bg-green-600/20' };
  };

  const getAccountStatus = (user) => {
    if (user.isDeleted) return { text: 'Fjernet', color: 'bg-orange-600/20 text-orange-400' };
    if (user.isSuspended) return { text: 'Suspenderet', color: 'bg-amber-600/20 text-amber-400' };
    if (user.isEmailVerified) return { text: 'Verificeret', color: 'bg-green-600/20 text-green-400' };
    return { text: 'Ikke verificeret', color: 'bg-yellow-600/20 text-yellow-400' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Brugerstyring</h1>
        <p className="text-slate-400 mt-1">Håndter brugerkonti og roller</p>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Søg efter brugernavn eller email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="all">Alle Brugere</option>
            <option value="creators">Skabere</option>
            <option value="listeners">Lyttere</option>
            <option value="admins">Administratorer</option>
            <option value="suspended">Suspenderede</option>
            <option value="removed">Fjernede</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Samlede Brugere</span>
          </div>
          <p className="text-xl font-bold text-white mt-1">{users.length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Skabere</span>
          </div>
          <p className="text-xl font-bold text-white mt-1">{users.filter(u => u.isCreator).length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <span className="text-slate-400 text-sm">Suspenderede</span>
          </div>
          <p className="text-xl font-bold text-white mt-1">{users.filter(u => u.isSuspended).length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-red-400" />
            <span className="text-slate-400 text-sm">Admins</span>
          </div>
          <p className="text-xl font-bold text-white mt-1">{users.filter(u => u.isAdmin).length}</p>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Bruger</th>
                <th className="text-left p-4 text-slate-300 font-medium">Rolle</th>
                <th className="text-left p-4 text-slate-300 font-medium">Registreret</th>
                <th className="text-left p-4 text-slate-300 font-medium">Land</th>
                <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                <th className="text-left p-4 text-slate-300 font-medium">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const role = getUserRole(user);
                const status = getAccountStatus(user);
                return (
                  <tr key={user.id} className={`border-t border-slate-700 hover:bg-slate-750 ${user.isDeleted ? 'opacity-50' : ''}`}>
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{user.username}</p>
                        <div className="flex items-center space-x-1 text-slate-400 text-sm">
                          <Mail className="w-3 h-3" />
                          <span>{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${role.color}`}>{role.text}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1 text-slate-400 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(user.dateJoined)}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300">{user.country || 'Ukendt'}</span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${status.color}`}>{status.text}</span>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => { setSelectedUser(user); setPurgeInput(''); setPurgeConfirmOpen(false); }}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                      >
                        Rediger
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Ingen brugere fundet</p>
          </div>
        )}
      </div>

      {/* User Edit Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-lg max-w-md w-full my-auto">
            <div className="p-6 border-b border-slate-700">
              <h3 className="text-xl font-bold text-white">Rediger Bruger</h3>
              <p className="text-slate-400 mt-1">{selectedUser.username} — {selectedUser.email}</p>
            </div>

            <div className="p-6 space-y-5">
              {/* Current status */}
              <div className="flex gap-3">
                <span className={`px-2 py-1 rounded-full text-xs ${getUserRole(selectedUser).color}`}>
                  {getUserRole(selectedUser).text}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs ${getAccountStatus(selectedUser).color}`}>
                  {getAccountStatus(selectedUser).text}
                </span>
              </div>

              {/* Role changes */}
              <div>
                <p className="text-slate-400 text-sm mb-2">Roller</p>
                <div className="flex flex-wrap gap-2">
                  {!selectedUser.isCreator && (
                    <button
                      onClick={() => updateUserRole(selectedUser.id, { isCreator: true })}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                    >
                      Gør til Skaber
                    </button>
                  )}
                  {!selectedUser.isAdmin && (
                    <button
                      onClick={() => updateUserRole(selectedUser.id, { isAdmin: true })}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                    >
                      Gør til Admin
                    </button>
                  )}
                  {(selectedUser.isCreator || selectedUser.isAdmin) && (
                    <button
                      onClick={() => updateUserRole(selectedUser.id, { isCreator: false, isAdmin: false })}
                      className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded transition-colors"
                    >
                      Fjern special-roller
                    </button>
                  )}
                </div>
              </div>

              {/* ── Danger Zone ─────────────────────────────────────── */}
              <div className="border border-red-900/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm font-semibold uppercase tracking-wide">Danger Zone</span>
                </div>

                {/* Suspend / Unsuspend */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white text-sm font-medium">
                      {selectedUser.isSuspended ? 'Ophæv suspendering' : 'Suspender konto'}
                    </p>
                    <p className="text-slate-400 text-xs mt-0.5">
                      {selectedUser.isSuspended
                        ? 'Genaktiverer adgang til platformen.'
                        : 'Blokerer adgang uden at slette data. Kan fortrydes.'}
                    </p>
                  </div>
                  <button
                    disabled={actionLoading}
                    onClick={() => selectedUser.isSuspended ? unsuspendUser(selectedUser.id) : suspendUser(selectedUser.id)}
                    className={`shrink-0 px-3 py-1.5 text-sm rounded transition-colors ${
                      selectedUser.isSuspended
                        ? 'bg-green-700 hover:bg-green-600 text-white'
                        : 'bg-amber-600 hover:bg-amber-500 text-white'
                    }`}
                  >
                    {selectedUser.isSuspended ? 'Ophæv' : 'Suspender'}
                  </button>
                </div>

                {/* Soft delete */}
                {!selectedUser.isDeleted && (
                  <div className="flex items-start justify-between gap-4 pt-3 border-t border-slate-700">
                    <div>
                      <p className="text-white text-sm font-medium">Fjern profil</p>
                      <p className="text-slate-400 text-xs mt-0.5">
                        Markerer profilen som fjernet. Firebase Auth-kontoen bevares.
                      </p>
                    </div>
                    <button
                      disabled={actionLoading}
                      onClick={() => softDeleteUser(selectedUser.id)}
                      className="shrink-0 px-3 py-1.5 bg-orange-700 hover:bg-orange-600 text-white text-sm rounded transition-colors"
                    >
                      Fjern
                    </button>
                  </div>
                )}

                {/* Hard purge */}
                <div className="pt-3 border-t border-slate-700">
                  {!purgeConfirmOpen ? (
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-white text-sm font-medium">Slet al brugerdata</p>
                        <p className="text-slate-400 text-xs mt-0.5">
                          Sletter alle Firestore-dokumenter permanent. Kan ikke fortrydes.
                          Auth-kontoen skal herefter slettes manuelt i Firebase Console.
                        </p>
                      </div>
                      <button
                        onClick={() => setPurgeConfirmOpen(true)}
                        className="shrink-0 px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-sm rounded transition-colors"
                      >
                        Slet data
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-red-400 text-xs font-medium">
                        Skriv brugernavnet <strong>{selectedUser.username}</strong> for at bekræfte permanent sletning:
                      </p>
                      <input
                        type="text"
                        value={purgeInput}
                        onChange={e => setPurgeInput(e.target.value)}
                        placeholder={selectedUser.username}
                        className="w-full px-3 py-1.5 bg-slate-900 border border-red-700 rounded text-white text-sm focus:outline-none focus:border-red-500"
                      />
                      <div className="flex gap-2">
                        <button
                          disabled={purgeInput !== selectedUser.username || actionLoading}
                          onClick={() => purgeUser(selectedUser.id, selectedUser.username)}
                          className="flex-1 py-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded transition-colors"
                        >
                          Slet permanent
                        </button>
                        <button
                          onClick={() => { setPurgeConfirmOpen(false); setPurgeInput(''); }}
                          className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded transition-colors"
                        >
                          Annuller
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-700 flex justify-end">
              <button
                onClick={() => { setSelectedUser(null); setPurgeConfirmOpen(false); setPurgeInput(''); }}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm"
              >
                Luk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
